import { useEditor, EditorContent, Mark } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { 
  Bold, 
  Italic, 
  Heading1, 
  Heading2, 
  Heading3, 
  Heading4,
  Minus
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

// Custom Font Size Mark
const FontSize = Mark.create({
  name: 'fontSize',
  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },
  addAttributes() {
    return {
      size: {
        default: null,
        parseHTML: element => element.style.fontSize,
        renderHTML: attributes => {
          if (!attributes.size) return {}
          return { style: `font-size: ${attributes.size}` }
        },
      },
    }
  },
  parseHTML() {
    return [
      {
        style: 'font-size',
      },
    ]
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', HTMLAttributes, 0]
  },
  addCommands() {
    return {
      setFontSize: size => ({ chain }) => {
        return chain().setMark(this.name, { size }).run()
      },
      unsetFontSize: () => ({ chain }) => {
        return chain().unsetMark(this.name).run()
      },
    }
  },
})

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  className?: string;
  minimal?: boolean;
  disabled?: boolean;
}

export const RichTextEditor = ({
  content,
  onChange,
  placeholder = "Type something...",
  className,
  minimal = false,
  disabled = false,
}: RichTextEditorProps) => {
  const [showToolbar, setShowToolbar] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
        bulletList: false,
        orderedList: false,
      }),
      Placeholder.configure({
        placeholder,
      }),
      FontSize,
    ],
    editable: !disabled,
    content: content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    onSelectionUpdate: ({ editor }) => {
      if (disabled) {
        setShowToolbar(false);
        return;
      }
      setShowToolbar(!editor.state.selection.empty);
    },
    onBlur: () => {
      setShowToolbar(false);
    },
    editorProps: {
      attributes: {
        class: cn(
          "w-full focus:outline-none transition-all prose max-w-none text-center",
          !minimal &&
            "min-h-[120px] p-4 bg-white border border-black/5 rounded-2xl text-sm prose-sm",
          className
        ),
      },
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  if (!editor) return null;

  return (
    <div className={cn("relative group/rte w-full", disabled && "pointer-events-none")}>
      <div
        className={cn(
          "flex items-center gap-1 p-0.5 sm:p-1 bg-black/90 backdrop-blur-md rounded-xl shadow-2xl border border-white/10 transition-all duration-200 z-[100] overflow-x-auto max-w-[95vw] md:max-w-none",
          "absolute -top-14 left-1/2 -translate-x-1/2",
          (showToolbar && !disabled) ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
        )}
      >
        <div className="flex items-center gap-1 px-1">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={cn(
              "p-1.5 rounded-lg transition-all",
              editor.isActive("bold") ? "bg-white text-black shadow-sm" : "hover:bg-white/10 text-white/60"
            )}
          >
            <Bold size={14} />
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={cn(
              "p-1.5 rounded-lg transition-all",
              editor.isActive("italic") ? "bg-white text-black shadow-sm" : "hover:bg-white/10 text-white/60"
            )}
          >
            <Italic size={14} />
          </button>
        </div>

        <div className="w-px h-4 bg-white/20" />

        <div className="flex items-center gap-1 px-1">
          {[1, 2, 3, 4].map((level) => (
            <button
              key={level}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleHeading({ level: level as any }).run()}
              className={cn(
                "p-1.5 rounded-lg transition-all text-[10px] font-bold w-7 h-7 flex items-center justify-center",
                editor.isActive("heading", { level }) ? "bg-white text-black shadow-sm" : "hover:bg-white/10 text-white/60"
              )}
            >
              H{level}
            </button>
          ))}
        </div>

        <div className="w-px h-4 bg-white/20" />

        {/* Font Size */}
        <div className="flex items-center gap-1 px-1">
          {[
            { label: 'S', size: '0.8em' },
            { label: 'M', size: '1em' },
            { label: 'L', size: '1.5em' },
            { label: 'XL', size: '2.5em' },
          ].map((item) => (
            <button
              key={item.label}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => (editor as any).chain().focus().setFontSize(item.size).run()}
              className={cn(
                "p-1.5 rounded-lg transition-all text-[10px] font-bold w-7 h-7 flex items-center justify-center",
                editor.isActive('fontSize', { size: item.size }) ? "bg-white text-black shadow-sm" : "hover:bg-white/10 text-white/60"
              )}
            >
              {item.label}
            </button>
          ))}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => (editor as any).chain().focus().unsetFontSize().run()}
            className="p-1.5 rounded-lg hover:bg-white/10 text-white/60"
          >
            <Minus size={10} />
          </button>
        </div>
      </div>

      <EditorContent editor={editor} />
    </div>
  );
};
